/**
 * Static Study · Practice · Master catalog (feature 023).
 *
 * This file is the single source of truth for PRESENTATION — titles, subtitles, ordering,
 * enabled/COMING SOON state, and question formats. The API deliberately does not repeat any
 * of it (research R7 / analysis finding D1).
 *
 * Structure and pool rules are mirrored on the backend in
 * `src/HawkesBridge.Core/Models/ModulePractice/ModuleCatalog.cs`. Slugs are a persisted
 * contract — `module_hand_history.module_key` stores `{family}.{topic}.{module}`, so renaming
 * a slug orphans that module's history.
 *
 * Bid tokens (`1C`, `1NT`, …) are kept in token form and rendered through the shared
 * `SuitSymbol` component per the constitution's suit-symbol standard — never as literal glyphs.
 */

export type ModuleFamily = 'precision' | 'competitive';

export type QuestionFormat = 'yes-no' | 'call-pad' | 'multi-step';

/**
 * The inventory row glyphs a topic without an opening bid can ask for (client request,
 * 2026-10-15). Names, not markup: the row component (`InventoryRow`) owns how each is drawn.
 * `transfer` is a pair of arrows. (Preempts had a `preempt` glyph here until it moved to its own
 * dashboard card on 2026-10-16; the card draws its own, richer icon.)
 */
export type TopicIcon = 'transfer';

export interface TopicDefinition {
  /** Url/storage slug, e.g. `1c`. */
  key: string;
  /** Opening call in bid-token form; null for topics without a single opening. */
  openingBid: string | null;
  /** Second opening for two-bid topics (majors: `1S`). */
  alternateOpeningBid: string | null;
  /** Inventory row title. Bid tokens inside are rendered via SuitSymbol by the row component. */
  title: string;
  /** Small print under the title in the inventory row. */
  subtitle: string;
  /** False for COMING SOON topics — the row does not navigate (FR-010). */
  enabled: boolean;
  /**
   * The inventory row's glyph for a topic with no opening bid to show (client request,
   * 2026-10-15). Opening-bid topics draw their bid token instead and leave this unset; a topic
   * with neither falls back to the row's brain glyph, which the COMING SOON rows keep.
   */
  icon?: TopicIcon;
  /**
   * The Precision notes chapter this topic studies, or null when no chapter covers it.
   * `slug` is the Learn manifest slug the topic page's study link opens at `/learn/{slug}`;
   * `order` is that chapter's number, which pins the mapping to a specific chapter rather
   * than to a slug string that could be repointed unnoticed.
   *
   * The pair is kept together rather than as two optional fields so a topic can never
   * carry a number without a destination. Both values are a contract with
   * `HawkesBridge.Api/Content/learn/manifest.json`; `moduleCatalog.chapters.test.ts`
   * fails if they drift from it.
   */
  chapter: { order: number; slug: string } | null;
  /**
   * The inventories that list this topic (feature 033, FR-016/FR-023). Omitted means both.
   * `['competitive']` for a competition-only topic (Transfers & UMJOODO); `[]` for a hidden
   * topic a dashboard card opens directly (Overcalls; Preempts since 2026-10-16). Listing is
   * presentation only — the topic page and its modules exist for any family that has a module
   * list below, and a hidden topic's page shows every family's rows together (`rowsFor`).
   */
  listedIn?: ModuleFamily[];
  /**
   * Per-family module lists for topics that do not use the standard five (feature 033,
   * FR-001). Omitted means the family's standard list (every opening-bid topic). When set it
   * REPLACES the standard list: a family missing from it has no modules in this topic.
   * Mirrored on the backend in `ModuleCatalog.cs` (`TopicDefinition.Modules`).
   */
  modules?: Partial<Record<ModuleFamily, ModuleDefinition[]>>;
  /**
   * Where the topic page's back link goes. Omitted means the family inventory
   * (`/inventory/{family}`); the hidden Overcalls topic goes back to `/dashboard`.
   */
  backTo?: string;
  /**
   * A heading per family for a page that shows both families' rows (feature 033, FR-017,
   * 2026-10-19): the topic page draws a labelled box around that family's rows. Presentation
   * only — a group has no key, pool, history or Practice of its own, and the API never sees it.
   * Omitted (every other topic) means the rows render plain, as they always have.
   */
  groups?: Partial<Record<ModuleFamily, string>>;
}

export interface ModuleDefinition {
  /** Url/storage slug, e.g. `opening-bid`. */
  slug: string;
  /** Row label on the topic page. `{bid}` is replaced with the topic's opening bid token. */
  nameTemplate: string;
  format: QuestionFormat;
  /**
   * Set only for modules that reuse an existing feature instead of serving their own pool.
   * With `variants`, the competitive "Opener's 3rd bid" opens a chooser for its two sub-modes
   * (FR-027); without them, the row's Practice button is a plain link to `route` (feature
   * 033's "Jeff's problems" row).
   */
  redirect?: {
    route: string;
    /** The existing sub-modes a chooser offers; omitted for a plain link. */
    variants?: { label: string; subMode: string }[];
  };
}

// Feature 033 (US2): the Preempts topic's own modules — one per AI sub-category — shared by its
// two family lists. The slugs are persisted keys (v6.7.0); do not rename.
const PREEMPTIVE_OPENINGS: ModuleDefinition = {
  slug: 'pre-open',
  nameTemplate: 'Preemptive openings (2H/2S, 3C to 3S)',
  format: 'call-pad',
};
const RESPONDING_TO_PREEMPT: ModuleDefinition = {
  slug: 'resp-2m',
  nameTemplate: "Responding to partner's 2H/2S preempt",
  format: 'call-pad',
};
const PREEMPTIVE_JUMP_OVERCALLS: ModuleDefinition = {
  slug: 'pre-jump',
  nameTemplate: 'Preemptive jump overcalls',
  format: 'call-pad',
};

/**
 * Inventory display order (FR-009/FR-010). Feature 033's competition-only Transfers & UMJOODO
 * follows the 2D opening directly, so the two COMING SOON topics (Slam, Counting) close both
 * lists (client request, 2026-10-15); Preempts sits in its catalog place but is listed nowhere
 * since it moved to a dashboard card (2026-10-16), and the hidden Overcalls topic comes last.
 * `topicsListedIn` keeps the hidden and competition-only topics out of the inventories they do
 * not belong to.
 */
export const TOPICS: TopicDefinition[] = [
  {
    key: '1c',
    openingBid: '1C',
    alternateOpeningBid: null,
    title: 'The 1C opening — 16+ points',
    subtitle: 'opening, responses, rebids, complete auctions',
    enabled: true,
    chapter: { order: 1, slug: 'should-you-open-1c' },
  },
  {
    key: '1d',
    openingBid: '1D',
    alternateOpeningBid: null,
    title: 'The 1D opening',
    subtitle: 'opening, responses, rebids, complete auctions',
    enabled: true,
    chapter: { order: 2, slug: '1d-responses' },
  },
  {
    key: 'majors',
    openingBid: '1H',
    alternateOpeningBid: '1S',
    title: 'Major suit openings',
    subtitle: 'opening, responses, raises, rebids',
    enabled: true,
    chapter: { order: 3, slug: 'responses-to-major-suit-openers' },
  },
  {
    key: '1nt',
    openingBid: '1NT',
    alternateOpeningBid: null,
    title: 'The no trump structure',
    subtitle: 'ranges, responses, invitations, rebids',
    enabled: true,
    chapter: { order: 4, slug: 'no-trump-structure' },
  },
  {
    key: '2c',
    openingBid: '2C',
    alternateOpeningBid: null,
    title: 'The 2C opening',
    subtitle: 'opening, responses, rebids',
    enabled: true,
    chapter: { order: 5, slug: '2c-opening' },
  },
  {
    key: '2d',
    openingBid: '2D',
    alternateOpeningBid: null,
    title: 'The 2D opening',
    subtitle: 'opening, responses, rebids',
    enabled: true,
    chapter: { order: 6, slug: '2d-opening' },
  },
  {
    // Feature 033 (US2, client 2026-10-16 / user 2026-10-19): a dashboard topic — listed in no
    // inventory, opened from the dashboard's "Preempts" card, back link to the dashboard. Its
    // page shows BOTH family lists together, each inside a labelled group: "Non-competitive"
    // holds the Precision-family rows (uncontested deals) and "Competitive" the Competitive-
    // family rows (contested deals); the backend's family filter does the split, and jump
    // overcalls are contested by nature so they appear only under Competitive. The family half
    // of a row's key IS its group — never key a row under the other family.
    key: 'preempts',
    openingBid: null,
    alternateOpeningBid: null,
    title: 'Preempts',
    subtitle: 'openings, responses, jump overcalls — with and without competition',
    enabled: true,
    chapter: { order: 7, slug: 'preempts' },
    listedIn: [],
    backTo: '/dashboard',
    groups: { precision: 'Non-competitive', competitive: 'Competitive' },
    modules: {
      precision: [PREEMPTIVE_OPENINGS, RESPONDING_TO_PREEMPT],
      competitive: [PREEMPTIVE_OPENINGS, RESPONDING_TO_PREEMPT, PREEMPTIVE_JUMP_OVERCALLS],
    },
  },
  {
    // Feature 033 (US3): competition-only — every deal has an opponent acting right after
    // partner's opening, so it can never appear in the Precision inventory (FR-022/FR-023).
    // Spans chapters 2 and 3 of the notes, so no single chapter link (FR-025).
    key: 'transfers',
    openingBid: null,
    alternateOpeningBid: null,
    title: 'Transfers & UMJOODO',
    subtitle: "partner's 1H/1S or 1D doubled or overcalled",
    enabled: true,
    icon: 'transfer',
    chapter: null,
    listedIn: ['competitive'],
    modules: {
      competitive: [
        { slug: 'partner-1m-doubled', nameTemplate: "Partner's 1H/1S doubled", format: 'call-pad' },
        { slug: 'partner-1m-overcalled', nameTemplate: "Partner's 1H/1S overcalled", format: 'call-pad' },
        { slug: 'partner-1d-doubled', nameTemplate: "Partner's 1D doubled", format: 'call-pad' },
        {
          slug: 'partner-1d-overcalled-clubs',
          nameTemplate: "Partner's 1D overcalled 2C/3C",
          format: 'call-pad',
        },
      ],
    },
  },
  {
    key: 'slam',
    openingBid: null,
    alternateOpeningBid: null,
    title: 'Slam bidding agreements',
    subtitle: 'in development with Jeff',
    enabled: false,
    // Chapter 8 — same as preempts above.
    chapter: { order: 8, slug: 'slam-bidding-agreements' },
  },
  {
    key: 'counting',
    openingBid: null,
    alternateOpeningBid: null,
    title: 'Counting · Play of the hand · Game/Slam bidding',
    subtitle: 'in development with Jeff',
    enabled: false,
    // The notes stop at chapter 8 — no chapter covers this topic, so its page shows
    // no study link at all rather than pointing at someone else's chapter.
    chapter: null,
  },
  {
    // Feature 033 (US1): the hidden Overcalls inventory behind the dashboard's "Competitive
    // bidding: The overcalling side" card. Listed in no inventory; its back link returns to
    // the dashboard. The title is what the module-practice trail shows after "Competitive
    // Bidding ·", which keeps today's wording. The rows are the category's two situation
    // sub-categories the client asked for, then "Others" for the rest (client, 2026-10-16);
    // no row names a call, so a row name cannot hand the learner the answer (FR-009/FR-010).
    key: 'overcalls',
    openingBid: null,
    alternateOpeningBid: null,
    title: 'The overcalling side',
    subtitle: "Jeff's problems, then his real overcall decisions",
    enabled: true,
    chapter: null,
    listedIn: [],
    backTo: '/dashboard',
    modules: {
      competitive: [
        {
          slug: 'jeffs-problems',
          nameTemplate: "Jeff's problems",
          format: 'call-pad',
          // No variants: a plain Practice link to the existing page (teaching problems only).
          redirect: { route: '/problems?mode=overcalling' },
        },
        { slug: 'over-1nt', nameTemplate: 'Over their 1NT opening', format: 'call-pad' },
        { slug: 'over-2m', nameTemplate: 'Over their preemptive 2H/2S', format: 'call-pad' },
        { slug: 'others', nameTemplate: 'Others', format: 'call-pad' },
      ],
    },
  },
];

/** FR-014 — every precision module serves auctions WITHOUT competition. */
const PRECISION_MODULES: ModuleDefinition[] = [
  { slug: 'opening-bid', nameTemplate: 'Opening bid', format: 'yes-no' },
  { slug: 'responding', nameTemplate: 'Responding to {bid}', format: 'call-pad' },
  { slug: 'openers-rebid', nameTemplate: "Opener's rebid after {bid}", format: 'call-pad' },
  { slug: 'responders-rebid', nameTemplate: "Responder's re-bid", format: 'call-pad' },
  { slug: 'complete-auction', nameTemplate: 'Complete Auction', format: 'multi-step' },
];

/** FR-015 — every competitive module serves auctions WITH the opponents bidding. */
const COMPETITIVE_MODULES: ModuleDefinition[] = [
  { slug: 'responders-initial-bid', nameTemplate: "Responder's initial bid", format: 'call-pad' },
  { slug: 'openers-rebid', nameTemplate: "Opener's rebid", format: 'call-pad' },
  { slug: 'responders-rebid', nameTemplate: "Responder's rebid", format: 'call-pad' },
  {
    slug: 'openers-3rd-bid',
    nameTemplate: "Opener's 3rd bid",
    format: 'call-pad',
    redirect: {
      route: '/problems',
      variants: [
        { label: 'Overcalled bids', subMode: 'overcalled' },
        { label: 'Precision opening bids', subMode: 'precision' },
      ],
    },
  },
  { slug: 'complete-auction', nameTemplate: 'Complete auction', format: 'multi-step' },
];

export const FAMILY_TITLES: Record<ModuleFamily, string> = {
  precision: 'The Precision System — your inventory',
  competitive: 'Competitive bidding — your inventory',
};

export const FAMILY_SUBTITLES: Record<ModuleFamily, string> = {
  precision: 'Everything you are going to learn, in one list. Pick a bid.',
  competitive: 'The same list, for auctions where the opponents fight back. Pick a bid.',
};

/** Compact family labels for the phone masthead trail (Header.tsx) — FAMILY_TITLES are
 *  written for the inventory page and are too long for a 2rem header line. */
export const FAMILY_SHORT_TITLES: Record<ModuleFamily, string> = {
  precision: 'Precision system',
  competitive: 'Competitive Bidding',
};

/**
 * The module list a topic offers in a family: the topic's own list when it carries one
 * (feature 033), otherwise the family's standard list, identical for every opening-bid topic.
 * Without a topic, the standard list. Mirrors `ModuleCatalog.ModulesFor` on the backend;
 * slugs are a persisted contract (see the file header).
 */
export function modulesFor(family: ModuleFamily, topic?: TopicDefinition): ModuleDefinition[] {
  if (topic?.modules) {
    return topic.modules[family] ?? [];
  }
  return family === 'precision' ? PRECISION_MODULES : COMPETITIVE_MODULES;
}

/** One row of a topic page: the module and the family its Practice link runs under. */
export interface TopicRow {
  family: ModuleFamily;
  module: ModuleDefinition;
}

/**
 * The rows a topic page shows at `/inventory/{pageFamily}/{topic}`. An inventory topic shows
 * the page family's list. A hidden topic (`listedIn: []`) belongs to no inventory, so its page
 * shows EVERY family's rows together — precision first — each row under its own family; both
 * family addresses render the same page (feature 033, FR-017: Preempts' "Non-competitive" is
 * the precision module and "Competitive" the competitive one). The module key, Back target and
 * session summary all follow the row's family, not the address's.
 */
export function rowsFor(topic: TopicDefinition, pageFamily: ModuleFamily): TopicRow[] {
  if (topic.listedIn && topic.listedIn.length === 0 && topic.modules) {
    const families: ModuleFamily[] = ['precision', 'competitive'];
    return families.flatMap((family) =>
      (topic.modules?.[family] ?? []).map((module) => ({ family, module }))
    );
  }
  return modulesFor(pageFamily, topic).map((module) => ({ family: pageFamily, module }));
}

/** The rows of one family on a topic page, with the heading the page boxes them under (or none). */
export interface TopicRowGroup {
  family: ModuleFamily;
  /** From `TopicDefinition.groups`; null means the rows render plain, without a box. */
  heading: string | null;
  rows: TopicRow[];
}

/**
 * `rowsFor` cut by family, in family order, for the topic page to draw (feature 033, FR-017,
 * 2026-10-19). A family with no rows gets no group. Only a topic that declares `groups` gets
 * headings — Preempts, whose page shows its Precision rows under "Non-competitive" and its
 * Competitive rows under "Competitive"; every other topic yields one heading-less group and
 * renders exactly as before.
 */
export function rowGroupsFor(topic: TopicDefinition, pageFamily: ModuleFamily): TopicRowGroup[] {
  const families: ModuleFamily[] = ['precision', 'competitive'];
  const rows = rowsFor(topic, pageFamily);
  return families
    .map((family) => ({
      family,
      heading: topic.groups?.[family] ?? null,
      rows: rows.filter((r) => r.family === family),
    }))
    .filter((group) => group.rows.length > 0);
}

export function findTopic(topicKey: string | undefined): TopicDefinition | undefined {
  return TOPICS.find((t) => t.key === topicKey);
}

/** Resolves a module through its topic's list — mirrors `ModuleCatalog.FindModule`. */
export function findModule(
  family: ModuleFamily,
  topic: TopicDefinition,
  moduleSlug: string | undefined
): ModuleDefinition | undefined {
  return modulesFor(family, topic).find((m) => m.slug === moduleSlug);
}

/** The topics an inventory page lists, in catalog order (feature 033, FR-016/FR-023). */
export function topicsListedIn(family: ModuleFamily): TopicDefinition[] {
  return TOPICS.filter((t) => !t.listedIn || t.listedIn.includes(family));
}

export function isFamily(value: string | undefined): value is ModuleFamily {
  return value === 'precision' || value === 'competitive';
}

/** `{family}.{topic}.{module}` — matches the backend ModuleKey and the stored history key. */
export function moduleKey(family: ModuleFamily, topicKey: string, moduleSlug: string): string {
  return `${family}.${topicKey}.${moduleSlug}`;
}

/**
 * Resolves a module's display name, substituting the topic's opening bid where the template
 * asks for it. The returned string still carries bid tokens for SuitSymbol rendering.
 */
export function moduleName(module: ModuleDefinition, topic: TopicDefinition): string {
  if (!module.nameTemplate.includes('{bid}')) {
    return module.nameTemplate;
  }
  const bid = topic.openingBid ?? '';
  const both = topic.alternateOpeningBid ? `${bid}/${topic.alternateOpeningBid}` : bid;
  return module.nameTemplate.replace('{bid}', both);
}

/** The question prompt for an opening-bid module (FR-016/FR-017a). */
export function openingBidPrompt(topic: TopicDefinition): string {
  const bid = topic.openingBid ?? '';
  const both = topic.alternateOpeningBid ? `${bid} or ${topic.alternateOpeningBid}` : bid;
  return `Should you open ${both}?`;
}
